import { ExternalLinkIcon } from "lucide-react";

import { ChangedFieldsRow } from "./ChangedFieldsRow";
import { Column, InfoBox, PlacementStatusBadge } from "@/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { formatting } from "@/lib/formatting";
import { CandidacyPlacement, CompensationType, Job } from "@/types";

import { JobSchema } from "../JobForm";

export const RevenueSection = ({
  placements,
  originalJob,
  formValues,
  companyId,
  hasBackfillChanged,
  hasRevenueAmountChanged,
  hasCompChanges,
}: {
  placements: CandidacyPlacement[];
  originalJob: Job;
  formValues: JobSchema;
  companyId: string;
  hasBackfillChanged: boolean;
  hasRevenueAmountChanged: boolean;
  hasCompChanges: boolean;
}) => {
  const { userNameMap } = useTaggableUsers();

  const originalCompSuffix =
    originalJob.compensationType === CompensationType.HOURLY ? "/hr" : "/yr";
  const compSuffix =
    formValues.compensationType === CompensationType.HOURLY ? "/hr" : "/yr";

  const hasUpdatableChanges = hasRevenueAmountChanged || hasCompChanges;

  return (
    <Column className="gap-3">
      {(hasBackfillChanged || hasUpdatableChanges) && (
        <Column className="gap-2">
          {hasUpdatableChanges && (
            <InfoBox>
              Revenue Earned and Compensation will not affect existing
              placements. However, staffers can update them directly if needed
              on each placement using the links in the table below.
            </InfoBox>
          )}
          {hasBackfillChanged && (
            <InfoBox>
              Backfill guarantee changes won&apos;t automatically affect
              existing placements. If this should affect any of the placements
              listed below, please screenshot to send to
              #staffer-or-recruiter-feedback tagging @matt. Otherwise, the
              backfill guarantee will remain the same for old placements.
            </InfoBox>
          )}
          {hasBackfillChanged && (
            <ChangedFieldsRow
              label="Backfill Guarantee Days"
              oldValue={originalJob.backfillGuaranteeDays?.toString() ?? "None"}
              newValue={formValues.backfillGuaranteeDays?.toString() ?? "None"}
            />
          )}
          {hasRevenueAmountChanged && (
            <ChangedFieldsRow
              label="Revenue Amount"
              oldValue={formatting.currency(originalJob.estimatedRevenueAmount)}
              newValue={formatting.currency(
                formValues.estimatedRevenueAmount ?? null,
              )}
            />
          )}
          {originalJob.compensationMin !== formValues.compensationMin && (
            <ChangedFieldsRow
              label="Compensation Min"
              oldValue={`${formatting.currency(originalJob.compensationMin)}${originalCompSuffix}`}
              newValue={`${formatting.currency(formValues.compensationMin)}${compSuffix}`}
            />
          )}
          {originalJob.compensationMax !== formValues.compensationMax && (
            <ChangedFieldsRow
              label="Compensation Max"
              oldValue={`${formatting.currency(originalJob.compensationMax)}${originalCompSuffix}`}
              newValue={`${formatting.currency(formValues.compensationMax)}${compSuffix}`}
            />
          )}
        </Column>
      )}
      <div className="rounded-md border border-slate-200">
        <Table className="w-full text-sm">
          <TableHeader>
            <TableRow>
              <TableHead>Candidacy</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Revenue Earned</TableHead>
              <TableHead>Compensation</TableHead>
              <TableHead>Link</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {placements.map((placement) => (
              <TableRow
                key={placement.id}
                className="border-t border-slate-200"
              >
                <TableCell>
                  {placement.candidacy.firstName} {placement.candidacy.lastName}
                </TableCell>
                <TableCell>
                  <PlacementStatusBadge status={placement.status} />
                </TableCell>
                <TableCell>{userNameMap[placement.ownerUserId]}</TableCell>
                <TableCell>
                  {formatting.currency(placement.revenueEarned)}
                </TableCell>
                <TableCell>
                  {formatting.currency(placement.baseCompensation)}
                </TableCell>
                <TableCell>
                  <a
                    href={`/companies/${companyId}/jobs/${placement.jobId}?c=${placement.candidacyId}&t=placements`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLinkIcon className="text-primary h-4 w-4" />
                  </a>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Column>
  );
};
